import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../hooks/useAuth';
import { Building2, Bed, Calendar, BarChart3, Plus, Eye, ClipboardList } from 'lucide-react';
import {
  ADMIN_ICON_STROKE,
  AdminPageHeader,
  AdminStatGrid,
  AdminIconBoxNeutral,
  adminCardClass
} from '../../components/admin/AdminShared';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalStays: 0,
    spotlightStays: 0,
    verifiedStays: 0,
    totalRooms: 0,
    totalCapacity: 0,
    averageRating: 0,
    totalUsers: 0,
    activeBookings: 0,
    totalBookings: 0,
    totalEvents: 0
  });
  const [loading, setLoading] = useState(true);

  const displayName = user?.name?.trim() || user?.phone || 'there';

  const dashboardCards = [
    {
      title: 'Manage Stays',
      description: 'Add, edit, and manage pet-friendly accommodations',
      icon: Building2,
      route: '/admin/stays',
      stats: `${stats.totalStays} active stays`
    },
    {
      title: 'Events',
      description: 'Create and manage pet events, shows, and tickets',
      icon: Calendar,
      route: '/admin/events',
      stats: `${stats.totalEvents ?? 0} active events`
    },
    {
      title: 'Rooms',
      description: 'Configure room types, pricing, and amenities',
      icon: Bed,
      route: '/admin/rooms',
      stats: `${stats.totalRooms} total rooms`
    },
    {
      title: 'Inventory',
      description: 'Manage room availability and capacity',
      icon: BarChart3,
      route: '/admin/inventory',
      stats: 'Availability & rates'
    },
    {
      title: 'Bookings',
      description: 'View stay reservations and status',
      icon: ClipboardList,
      route: '/admin/bookings',
      stats: `${stats.activeBookings ?? 0} active`
    }
  ];

  const quickActions = [
    {
      title: 'Add stay',
      description: 'Create a new listing',
      icon: Plus,
      action: () => navigate('/admin/stays?action=create')
    },
    {
      title: 'Add rooms',
      description: 'Configure rooms for a stay',
      icon: Bed,
      action: () => navigate('/admin/rooms?action=create')
    },
    {
      title: 'View bookings',
      description: 'Reservations and payments',
      icon: Eye,
      action: () => navigate('/admin/bookings')
    }
  ];

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await adminApi.getStats();
        setStats(response);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statItems = [
    { label: 'Stays', value: stats.totalStays, icon: Building2 },
    { label: 'Rooms', value: stats.totalRooms, icon: Bed },
    { label: 'Active bookings', value: stats.activeBookings, icon: Calendar },
    { label: 'Total bookings', value: stats.totalBookings, icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <AdminPageHeader
          title="Dashboard"
          subtitle={`Hi ${displayName} — manage stays, events, and bookings in one place.`}
        />

        <AdminStatGrid items={statItems} loading={loading} className="mb-10" />

        <div className="mb-10">
          <h2 className="text-[16px] font-medium text-black mb-4">Quick actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {quickActions.map((action, index) => (
              <button
                key={index}
                type="button"
                onClick={action.action}
                className="text-left bg-gradient-to-br from-[#FF5E4C] to-[#ff8a7a] text-white p-5 md:p-6 rounded-[16px] shadow-md hover:opacity-95 transition-opacity"
              >
                <div className="flex items-start gap-4">
                  <action.icon className="w-7 h-7 shrink-0 opacity-95" strokeWidth={ADMIN_ICON_STROKE} />
                  <div>
                    <h3 className="font-medium text-[15px]">{action.title}</h3>
                    <p className="text-[13px] opacity-90 mt-0.5 leading-snug">{action.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-[16px] font-medium text-black mb-4">All sections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dashboardCards.map((card, index) => (
              <button
                key={index}
                type="button"
                onClick={() => navigate(card.route)}
                className={`text-left ${adminCardClass} p-5 md:p-6 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start gap-4">
                  <AdminIconBoxNeutral>
                    <card.icon className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} />
                  </AdminIconBoxNeutral>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[15px] font-medium text-black mb-1">{card.title}</h3>
                    <p className="text-[13px] text-[#5c5c5c] leading-snug mb-2">{card.description}</p>
                    <p className="text-[12px] text-[#78736A]">{card.stats}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className={`${adminCardClass} p-5 md:p-6`}>
          <h2 className="text-[16px] font-medium text-black mb-4">Recent activity</h2>
          <p className="text-[14px] text-[#78736A]">No recent activity yet.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
